// Libs
import classNames from 'classnames/bind';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, List, ListItemButton, ListItemIcon, Typography } from '@mui/material';
// Components, Layouts, Pages
import { MenuItem } from '~/layouts/main/components';
// Others
import packageJson from '../../../../../package.json';
import { EMPTY_STRING } from '~/utils/constants/common';
import { StorageEnum } from '~/utils/enum/base';
import { RoleEnum } from '~/utils/enum/common';
import { IRouteModel } from '~/utils/interface/base';
import { authActions } from '~/thunks/auth/authSlice';
import { useAppDispatch } from '~/utils/redux/hooks';
import { authRouteAbsolute } from '~/utils/routes/auth';
import { privateCrewRoutes } from '~/utils/routes/privateRoleRoutes/crew';
import { privateAdminRoutes } from '~/utils/routes/privateRoleRoutes/admin';
import { privateLoaderRoutes } from '~/utils/routes/privateRoleRoutes/loader';
import { privateForemanRoutes } from '~/utils/routes/privateRoleRoutes/foreman';
import { privateSalesAdminRoutes } from '~/utils/routes/privateRoleRoutes/salesAdmin';
import { privateSalesPersonRoutes } from '~/utils/routes/privateRoleRoutes/salesPerson';
import { privateWarehouseLeadRoutes } from '~/utils/routes/privateRoleRoutes/warehouseLead';
import { privateWarehouseStaffRoutes } from '~/utils/routes/privateRoleRoutes/warehouseStaff';
import { privateOperationsManagerRoutes } from '~/utils/routes/privateRoleRoutes/operationsManager';
import { usePermissions } from '~/utils/hooks/userPermissions';
import {
  CLIENT_PERMISSIONS,
  EQUIPMENT_PERMISSIONS,
  INVOICE_PERMISSIONS,
  JOB_PERMISSIONS,
  LEAD_PERMISSIONS,
  ORDER_PERMISSIONS,
  PACKAGE_PERMISSIONS,
  PRODUCT_PERMISSIONS,
  PURCHASE_ORDER_PERMISSIONS,
  QUARANTINE_PERMISSIONS,
  QUOTE_PERMISSIONS,
  REPORT_PERMISSIONS,
  SCHEDULE_PERMISSIONS,
  TASK_PERMISSIONS,
  TRAILER_PERMISSIONS,
  TRUCK_PERMISSIONS,
  VENDOR_PERMISSIONS,
} from '~/utils/constants/permissions';
import { adminRoute } from '~/utils/routes/roleRoutes/admin';
// Styles, images, icons
import styles from './Sidebar.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const Sidebar = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  const currentRole: string = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0] ?? EMPTY_STRING;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/sidebar']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { canAllLeads, canCreateLeads, canViewLeads, canDetailLeads, canEditLeads, canDeleteLeads } =
    usePermissions(LEAD_PERMISSIONS);
  const { canAllClients, canCreateClients, canViewClients, canDetailClients, canEditClients, canDeleteClients } =
    usePermissions(CLIENT_PERMISSIONS);
  const {
    canAllSchedules,
    canCreateSchedules,
    canViewSchedules,
    canDetailSchedules,
    canEditSchedules,
    canDeleteSchedules,
  } = usePermissions(SCHEDULE_PERMISSIONS);
  const { canAllJobs, canViewAllJobs, canCreateJobs, canViewJobs, canDetailJobs, canEditJobs, canDeleteJobs } =
    usePermissions(JOB_PERMISSIONS);
  const {
    canAllTasks,
    // canViewAllTasks,
    // canAssignOverbookingTasks,
    canCreateTasks,
    canViewTasks,
    canDetailTasks,
    canEditTasks,
    canDeleteTasks,
  } = usePermissions(TASK_PERMISSIONS);
  const { canAllQuotes, canCreateQuotes, canViewQuotes, canDetailQuotes, canEditQuotes, canDeleteQuotes } =
    usePermissions(QUOTE_PERMISSIONS);
  const { canAllOrders, canCreateOrders, canViewOrders, canDetailOrders, canEditOrders, canDeleteOrders } =
    usePermissions(ORDER_PERMISSIONS);
  const { canAllInvoices, canCreateInvoices, canViewInvoices, canDetailInvoices, canEditInvoices, canDeleteInvoices } =
    usePermissions(INVOICE_PERMISSIONS);
  const { canAllProducts, canCreateProducts, canViewProducts, canDetailProducts, canEditProducts, canDeleteProducts } =
    usePermissions(PRODUCT_PERMISSIONS);
  const { canAllPackages, canCreatePackages, canViewPackages, canDetailPackages, canEditPackages, canDeletePackages } =
    usePermissions(PACKAGE_PERMISSIONS);
  const {
    canAllQuarantines,
    canCreateQuarantines,
    canViewQuarantines,
    canDetailQuarantines,
    canEditQuarantines,
    canDeleteQuarantines,
  } = usePermissions(QUARANTINE_PERMISSIONS);
  const { canAllReports, canCreateReports, canViewReports, canDetailReports, canEditReports, canDeleteReports } =
    usePermissions(REPORT_PERMISSIONS);
  const { canAllTrucks, canCreateTrucks, canViewTrucks, canDetailTrucks, canEditTrucks, canDeleteTrucks } =
    usePermissions(TRUCK_PERMISSIONS);
  const { canAllTrailers, canCreateTrailers, canViewTrailers, canDetailTrailers, canEditTrailers, canDeleteTrailers } =
    usePermissions(TRAILER_PERMISSIONS);
  const {
    canAllEquipments,
    canCreateEquipments,
    canViewEquipments,
    canDetailEquipments,
    canEditEquipments,
    canDeleteEquipments,
  } = usePermissions(EQUIPMENT_PERMISSIONS);
  const { canAllVendors, canCreateVendors, canViewVendors, canDetailVendors, canEditVendors, canDeleteVendors } =
    usePermissions(VENDOR_PERMISSIONS);
  const {
    canAllPurchaseOrders,
    canCreatePurchaseOrders,
    canViewPurchaseOrders,
    canDetailPurchaseOrders,
    canEditPurchaseOrders,
    canDeletePurchaseOrders,
  } = usePermissions(PURCHASE_ORDER_PERMISSIONS);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  const routes: IRouteModel[] = useMemo(() => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        return privateAdminRoutes;
      case RoleEnum.SALES_ADMIN:
        return privateSalesAdminRoutes;
      case RoleEnum.SALES_PERSON:
        return privateSalesPersonRoutes;
      case RoleEnum.OPERATIONS_MANAGER:
        return privateOperationsManagerRoutes;
      case RoleEnum.WAREHOUSE_LEAD:
        return privateWarehouseLeadRoutes;
      case RoleEnum.WAREHOUSE_STAFF:
        return privateWarehouseStaffRoutes;
      case RoleEnum.FOREMAN:
        return privateForemanRoutes;
      case RoleEnum.CREW:
        return privateCrewRoutes;
      case RoleEnum.LOADER:
        return privateLoaderRoutes;

      default:
        return privateAdminRoutes;
    }
  }, [currentRole]);
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleLogout = () => {
    localStorage.removeItem(StorageEnum.ACCESS_TOKEN);
    localStorage.removeItem(StorageEnum.REFRESH_TOKEN);
    localStorage.removeItem(StorageEnum.STATUS_LOGIN);
    localStorage.removeItem(StorageEnum.DEVICE_ID);
    localStorage.removeItem(StorageEnum.ROLE);
    navigate(authRouteAbsolute.login);

    dispatch(authActions.setAccessToken(null));
  };

  const handleShowSideBar = (path: string): boolean => {
    switch (path) {
      case adminRoute.crm:
        return (
          canAllLeads ||
          canCreateLeads ||
          canViewLeads ||
          canDetailLeads ||
          canEditLeads ||
          canDeleteLeads ||
          canAllClients ||
          canCreateClients ||
          canViewClients ||
          canDetailClients ||
          canEditClients ||
          canDeleteClients
        );

      case adminRoute.schedule:
        return (
          canAllSchedules ||
          canCreateSchedules ||
          canViewSchedules ||
          canDetailSchedules ||
          canEditSchedules ||
          canDeleteSchedules
        );

      case adminRoute.jobs:
        return (
          canAllJobs || canViewAllJobs || canCreateJobs || canViewJobs || canDetailJobs || canEditJobs || canDeleteJobs
        );

      case adminRoute.task:
        return canAllTasks || canCreateTasks || canViewTasks || canDetailTasks || canEditTasks || canDeleteTasks;

      case adminRoute.quotes:
        return canAllQuotes || canCreateQuotes || canViewQuotes || canDetailQuotes || canEditQuotes || canDeleteQuotes;

      case adminRoute.order:
        return canAllOrders || canCreateOrders || canViewOrders || canDetailOrders || canEditOrders || canDeleteOrders;

      case adminRoute.invoice:
        return (
          canAllInvoices ||
          canCreateInvoices ||
          canViewInvoices ||
          canDetailInvoices ||
          canEditInvoices ||
          canDeleteInvoices
        );

      case adminRoute.inventory:
        return (
          canAllProducts ||
          canCreateProducts ||
          canViewProducts ||
          canDetailProducts ||
          canEditProducts ||
          canDeleteProducts ||
          canAllPackages ||
          canCreatePackages ||
          canViewPackages ||
          canDetailPackages ||
          canEditPackages ||
          canDeletePackages ||
          canAllQuarantines ||
          canCreateQuarantines ||
          canViewQuarantines ||
          canDetailQuarantines ||
          canEditQuarantines ||
          canDeleteQuarantines
        );

      case adminRoute.reports:
        return (
          canAllReports || canCreateReports || canViewReports || canDetailReports || canEditReports || canDeleteReports
        );

      case adminRoute.vehicles:
        return (
          canAllTrucks ||
          canCreateTrucks ||
          canViewTrucks ||
          canDetailTrucks ||
          canEditTrucks ||
          canDeleteTrucks ||
          canAllTrailers ||
          canCreateTrailers ||
          canViewTrailers ||
          canDetailTrailers ||
          canEditTrailers ||
          canDeleteTrailers ||
          canAllEquipments ||
          canCreateEquipments ||
          canViewEquipments ||
          canDetailEquipments ||
          canEditEquipments ||
          canDeleteEquipments
        );

      case adminRoute.vendors:
        return (
          canAllVendors || canCreateVendors || canViewVendors || canDetailVendors || canEditVendors || canDeleteVendors
        );

      case adminRoute.purchaseOrder:
        return (
          canAllPurchaseOrders ||
          canCreatePurchaseOrders ||
          canViewPurchaseOrders ||
          canDetailPurchaseOrders ||
          canEditPurchaseOrders ||
          canDeletePurchaseOrders
        );

      default:
        return true;
    }
  };

  //#endregion Handle Function

  return (
    <Box id='sidebarMainLayout' className={cx('container')}>
      <Box className={cx('headerSection')}>
        <img src={icons.commonIconLogoFull} className={cx('image')} alt={t('common_alt_img', { ns: 'common/base' })} />
      </Box>

      <Box className={cx('menuSection')}>
        <Typography className={cx('groupTitle')}>{t('group_user_panel')}</Typography>

        <List component='div' className={cx('menuList')}>
          {routes.map((route: IRouteModel, index: number) => {
            if (!route.path || !route?.name || !route?.activeIcon || !route?.icon) return null;

            const path = route?.path ? `/${route.path.split('/').filter(Boolean).pop()}` : EMPTY_STRING;

            const isPermission = handleShowSideBar(path);

            if (isPermission === false) return null;

            return <MenuItem key={route.path} menuItem={route} />;
          })}
        </List>

        <ListItemButton className={cx('menuBtn')} onClick={handleLogout}>
          <ListItemIcon className={cx('menuIconWrap')}>
            <img
              src={icons.menuIconLogout}
              alt={t('common_alt_img', { ns: 'common/base' })}
              className={cx('menuItemIcon')}
            />
          </ListItemIcon>

          <Typography component='p' className={cx('menuItemText')}>
            {t('menu_logout')}
          </Typography>
        </ListItemButton>

        <Box className={cx('sidebarFooter')}>
          <Typography component='p' className={cx('copyright')}>
            &copy;{t('copyright', { ns: 'common/sidebar' })}
          </Typography>
          <Typography component='p' className={cx('version')}>
            {packageJson.version}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Sidebar;
